/**
 *
 * SignUpForm
 *
 */
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Control,
  Controller,
  FieldErrorsImpl,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';
import { ArrowLeftIcon } from '@radix-ui/react-icons';

import { PreloadedQuery } from 'react-relay';
import { countryCountriesQuery } from 'services/country/__generated__/countryCountriesQuery.graphql';

import { ButtonStyled } from 'app/components/ButtonStyled';
import { InputStyled } from 'app/components/InputStyled';
import { Alert } from 'app/components/Alert';
import { FormHeader, FormButtonContainer } from 'app/components/MultiStepForm/styles';

import { CountrySelect } from 'app/pages/AuthPages/containers/CountrySelect';

import { Div, FlexCol, FlexRow } from 'styles/__layout';
import { BaseText, SpanText, StyledLink } from 'styles/__typography';
import { Wrapper } from './styles';
import theme from 'styles/theme';
import { FormValues } from '../..';

interface Props {
  countriesQueryRef: PreloadedQuery<countryCountriesQuery>;
  setIsEmailSent: (val: boolean) => void;
  isEmailSent: boolean;
  handleSubmit: any;
  onSubmit: any;
  error: string;
  isLoading: boolean;
  control: Control<FormValues, any>;
  register: UseFormRegister<FormValues>;
  getValues: UseFormGetValues<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  errors: Partial<FieldErrorsImpl<FormValues>>;
  setError: UseFormSetError<FormValues>;
  isSubmitting: boolean;
  isValid: boolean;
  goTo?: (index: number) => void;
}

export const SignUpTerms = memo(() => {
  return (
    <Div>
      <SpanText fontSize={0} color="greys.0">
        By clicking sign up, you agree to Unmind’s{' '}
      </SpanText>
      <StyledLink fontSize={0} color="blues.2" textAlign="center" target="_blank" to="https://unmind.com/terms-of-use">
        Terms of Use
      </StyledLink>
    </Div>
  );
});

export const SignUpForm = memo(
  ({
    countriesQueryRef,
    isEmailSent,
    setIsEmailSent,
    handleSubmit,
    onSubmit,
    getValues,
    setValue,
    control,
    errors,
    setError,
    error,
    isValid,
    isSubmitting,
    isLoading,
    goTo,
  }: Props) => {
    const { t } = useTranslation();

    return (
      <Wrapper onSubmit={handleSubmit(onSubmit)}>
        {isEmailSent ? (
          <>
            <FormHeader mb={8}>Verify your email address</FormHeader>
            <BaseText fontSize={2} mb={3}>{`We have sent you an email to ${getValues('email')}`}</BaseText>
            <BaseText fontSize={2}>
              To get started with Unmind Connect, please click the link in the email and set up your account.{' '}
            </BaseText>
            <FormButtonContainer justifyContent="space-between" alignItems="center" mt={6}>
              <FlexRow alignItems="center" justifyContent="flex-start">
                <ArrowLeftIcon style={{ color: theme.colors.browns[4] }} />
                <ButtonStyled
                  variant="transparent"
                  px={0}
                  onClick={() => {
                    goTo && goTo(0);
                    setIsEmailSent(false);
                  }}
                >
                  <BaseText ml={1} color="browns.4">
                    Back to start
                  </BaseText>
                </ButtonStyled>
              </FlexRow>
              <ButtonStyled
                ml={6}
                type="submit"
                variant="secondary"
                minWidth="90px"
                disabled={!isValid || isSubmitting || isLoading}
                loading={isLoading || isSubmitting}
              >
                Resend email
              </ButtonStyled>
            </FormButtonContainer>
          </>
        ) : (
          <>
            <FormHeader>{isEmailSent ? 'Verify your email address' : 'Let’s get started!'}</FormHeader>
            {error && (
              <Alert
                mb={4}
                variant="error"
                message={error ?? ''}
                onClose={() =>
                  setError('email', {
                    message: '',
                  })
                }
              />
            )}
            <FlexCol>
              <CountrySelect control={control} setValue={setValue} countriesQueryRef={countriesQueryRef} />
              <Controller
                control={control}
                name="email"
                render={({ field: { onChange, onBlur, value }, fieldState: { isDirty, isTouched } }) => (
                  <InputStyled
                    label="Email"
                    error={isTouched && isDirty && Boolean(errors?.email?.message)}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    variant="primary"
                    width="100%"
                    height="48px"
                    type="text"
                  />
                )}
              />
              <FormButtonContainer justifyContent="space-between" mt={6}>
                <SignUpTerms />
                <ButtonStyled
                  ml={6}
                  type="submit"
                  variant="primary"
                  minWidth="90px"
                  disabled={!isValid || isSubmitting || isLoading}
                  loading={isLoading || isSubmitting}
                >
                  {t(`auth.button.sign_up`)}
                </ButtonStyled>
              </FormButtonContainer>
            </FlexCol>
          </>
        )}
      </Wrapper>
    );
  },
);
